import React, { useState, useEffect } from 'react';
import { MessageSquare, Filter, Send, Clock, CheckCircle, AlertCircle, User, Mail, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const FeedbackManager = () => {
  const [feedback, setFeedback] = useState([]);
  const [counts, setCounts] = useState({ new: 0, in_progress: 0, resolved: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', type: '' });
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [responses, setResponses] = useState([]);
  const [responseText, setResponseText] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchFeedback();
  }, [filter]);

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.type) params.append('feedback_type', filter.type);
      
      const response = await axiosInstance.get(`/feedback/admin/all?${params.toString()}`);
      setFeedback(response.data.feedback || []);
      setCounts(response.data.counts || { new: 0, in_progress: 0, resolved: 0, total: 0 });
    } catch (error) {
      toast.error('Fehler beim Laden des Feedbacks');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedbackDetail = async (feedbackId) => {
    try {
      const response = await axiosInstance.get(`/feedback/admin/${feedbackId}`);
      setSelectedFeedback(response.data.feedback);
      setResponses(response.data.responses || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Details');
    }
  };

  const updateStatus = async (feedbackId, status) => {
    try {
      await axiosInstance.put(`/feedback/admin/${feedbackId}/status?status=${status}`);
      toast.success('Status aktualisiert');
      fetchFeedback();
      if (selectedFeedback?.id === feedbackId) {
        setSelectedFeedback({ ...selectedFeedback, status });
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const sendResponse = async () => {
    if (!responseText.trim() || !selectedFeedback) return;
    
    try {
      await axiosInstance.post(`/feedback/admin/${selectedFeedback.id}/respond`, {
        response: responseText
      });
      toast.success('Antwort gesendet');
      setResponseText('');
      fetchFeedbackDetail(selectedFeedback.id);
      fetchFeedback();
    } catch (error) {
      toast.error('Fehler beim Senden');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-sky-50/70 text-sky-700';
      case 'in_progress': return 'bg-amber-50/70 text-amber-700';
      case 'resolved': return 'bg-emerald-50/70 text-emerald-700';
      case 'closed': return 'bg-slate-50/70 text-slate-700';
      default: return 'bg-slate-50/70 text-slate-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'new': return 'Neu';
      case 'in_progress': return 'In Bearbeitung';
      case 'resolved': return 'Gelöst';
      case 'closed': return 'Geschlossen';
      default: return status;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'general': return 'Allgemein';
      case 'product': return 'Artikel';
      case 'vendor': return 'Verkäufer';
      case 'order': return 'Bestellung';
      case 'suggestion': return 'Vorschlag';
      default: return type;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen py-4 px-3 sm:px-4" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-7xl mx-auto">
        <AdminPageHeader 
          title="Feedback-Verwaltung" 
          subtitle={`${counts.total} Einträge gesamt`}
          icon={MessageSquare}
        />

        {/* Section: Stats - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0F8FF]">
            <Filter className="h-4 w-4 text-[#60A0D0]" />
          </div>
          <h2 className="text-base font-semibold text-white">Status-Übersicht</h2>
        </div>

        {/* Stats - Very light pastel colors */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <Card className="rounded-none cursor-pointer hover:shadow-lg bg-slate-50/70 border-slate-100" onClick={() => setFilter({ ...filter, status: '' })}>
            <CardContent className="p-3 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-slate-500" />
              <div>
                <p className="text-xs text-gray-600">Gesamt</p>
                <p className="text-2xl font-bold text-gray-800">{counts.total}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-none cursor-pointer hover:shadow-lg bg-sky-50/70 border-sky-100" onClick={() => setFilter({ ...filter, status: 'new' })}>
            <CardContent className="p-3 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-sky-500" />
              <div>
                <p className="text-xs text-gray-600">Neu</p>
                <p className="text-2xl font-bold text-sky-600">{counts.new}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-none cursor-pointer hover:shadow-lg bg-amber-50/70 border-amber-100" onClick={() => setFilter({ ...filter, status: 'in_progress' })}>
            <CardContent className="p-3 flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-xs text-gray-600">In Bearbeitung</p>
                <p className="text-2xl font-bold text-gray-900">{counts.in_progress}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-none cursor-pointer hover:shadow-lg bg-emerald-50/70 border-emerald-100" onClick={() => setFilter({ ...filter, status: 'resolved' })}>
            <CardContent className="p-3 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
              <div>
                <p className="text-xs text-gray-600">Gelöst</p>
                <p className="text-2xl font-bold text-gray-900">{counts.resolved}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Section: Feedback List - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2 mt-4" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FFF0F5]">
            <MessageSquare className="h-4 w-4 text-[#D070A0]" />
          </div>
          <h2 className="text-base font-semibold text-white">Feedback-Liste</h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          {/* Feedback List */}
          <Card className="rounded-none">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Eingegangenes Feedback</CardTitle>
                <div className="flex gap-2">
                  <select
                    value={filter.type}
                    onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                    className="text-xs border border-[#D4C4B0] rounded-none p-1 bg-white"
                  >
                    <option value="">Alle Typen</option>
                    <option value="general">Allgemein</option>
                    <option value="product">Artikel</option>
                    <option value="vendor">Verkäufer</option>
                    <option value="order">Bestellung</option>
                    <option value="suggestion">Vorschlag</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="max-h-[600px] overflow-y-auto">
              {loading ? (
                <p className="text-center py-4 text-gray-500">Lädt...</p>
              ) : feedback.length === 0 ? (
                <p className="text-center py-8 text-gray-500">Kein Feedback vorhanden</p>
              ) : (
                <div className="space-y-2">
                  {feedback.map((item) => (
                    <div
                      key={item.id}
                      className={`border p-3 cursor-pointer hover:bg-amber-50/30 transition-colors rounded-none ${
                        selectedFeedback?.id === item.id ? 'border-amber-300 bg-amber-50/50' : 'border-[#E8DFD4]'
                      }`}
                      onClick={() => fetchFeedbackDetail(item.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded-none ${getStatusColor(item.status)}`}>
                              {getStatusLabel(item.status)}
                            </span>
                            <span className="text-xs text-gray-500">{getTypeLabel(item.feedback_type)}</span>
                          </div>
                          <h4 className="font-medium text-sm">{item.subject}</h4>
                          <p className="text-xs text-gray-600 line-clamp-1 mt-1">{item.message}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                            <User className="h-3 w-3" />
                            <span>{item.user_name || item.user_email || 'Anonym'}</span>
                            <Calendar className="h-3 w-3 ml-2" />
                            <span>{formatDate(item.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Detail View */}
          <Card className="rounded-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Details & Antworten</CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedFeedback ? (
                <p className="text-center py-8 text-gray-500">
                  Wählen Sie ein Feedback aus der Liste aus
                </p>
              ) : (
                <div className="space-y-4">
                  {/* Feedback Info */}
                  <div className="bg-gray-50 p-3 rounded-none">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(selectedFeedback.status)}`}>
                        {getStatusLabel(selectedFeedback.status)}
                      </span>
                      <select
                        value={selectedFeedback.status}
                        onChange={(e) => updateStatus(selectedFeedback.id, e.target.value)}
                        className="text-xs border rounded-none p-1"
                      >
                        <option value="new">Neu</option>
                        <option value="in_progress">In Bearbeitung</option>
                        <option value="resolved">Gelöst</option>
                        <option value="closed">Geschlossen</option>
                      </select>
                    </div>
                    <h3 className="font-semibold">{selectedFeedback.subject}</h3>
                    <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{selectedFeedback.message}</p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 border-t pt-2">
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {selectedFeedback.user_name || 'Anonym'}
                      </div>
                      {selectedFeedback.user_email && (
                        <div className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {selectedFeedback.user_email}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Responses */}
                  {responses.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Antworten:</h4>
                      {responses.map((response) => (
                        <div key={response.id} className="bg-green-50 p-3 rounded-none border-l-4 border-l-green-500">
                          <p className="text-sm">{response.response}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            {response.admin_name} - {formatDate(response.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Response Form */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-sm mb-2">Antwort senden:</h4>
                    <textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      placeholder="Ihre Antwort..."
                      className="w-full border rounded-none p-2 text-sm min-h-[100px]"
                    />
                    <Button
                      onClick={sendResponse}
                      disabled={!responseText.trim()}
                      className="mt-2 rounded-none"
                      className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Antwort senden
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FeedbackManager;
